import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { CORE, NEEVE, NEUTRAL } from '@/utils/dls-tokens';
import type { DeploymentStats } from '@/types/metrics';

interface DeploymentsChartProps {
  stats: DeploymentStats;
}

export function DeploymentsChart({ stats }: DeploymentsChartProps) {
  const availablePercent =
    stats.totalPkiSerials > 0
      ? Math.round((stats.totalAvailable / stats.totalPkiSerials) * 100)
      : 0;

  return (
    <Card>
      <CardHeader className="dls-flex dls-flex-row dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
            Deployments
            <InfoTooltip text="Nodes deployed over time based on first contact date. Available = unassigned, non-EOL serial numbers ready for deployment." />
          </CardTitle>
        </div>
        <div className="dls-flex dls-items-center dls-gap-6 dls-text-sm">
          <div>
            <span className="dls-text-muted-foreground">Total Edge Nodes Deployed: </span>
            <span className="dls-font-semibold">{stats.totalDeployed}</span>
          </div>
          <div>
            <span className="dls-text-muted-foreground">Available: </span>
            <span className="dls-font-semibold">
              {availablePercent}% of {stats.totalPkiSerials} Serials
            </span>
            <span className="dls-text-muted-foreground dls-ml-1">({stats.totalAvailable})</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {stats.deploymentsByQuarter.length === 0 ? (
          <p className="dls-text-muted-foreground dls-text-sm dls-text-center dls-py-8">
            No deployment data available yet.
          </p>
        ) : (
          <>
            <div className="dls-flex dls-items-center dls-justify-center dls-gap-6 dls-mb-3 dls-text-xs dls-text-muted-foreground">
              <div className="dls-flex dls-items-center dls-gap-1.5">
                <span
                  className="dls-inline-block dls-w-3 dls-h-3 dls-rounded-sm"
                  style={{ backgroundColor: CORE.neeveGreen }}
                />
                <span>Nodes Deployed (per quarter)</span>
              </div>
              <div className="dls-flex dls-items-center dls-gap-1.5">
                <span
                  className="dls-inline-block dls-w-4 dls-h-0.5 dls-rounded"
                  style={{ backgroundColor: NEEVE.orange }}
                />
                <span>Total Deployments (cumulative)</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart
                data={stats.deploymentsByQuarter}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="quarter" tick={{ fontSize: 12 }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  label={{
                    value: 'Per Quarter',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 11, fill: NEUTRAL.secondary },
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  label={{
                    value: 'Cumulative',
                    angle: 90,
                    position: 'insideRight',
                    style: { fontSize: 11, fill: NEUTRAL.secondary },
                  }}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '8px',
                    border: `1px solid ${NEUTRAL.tertiary}`,
                    fontSize: '13px',
                  }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="count"
                  name="Nodes Deployed"
                  fill={CORE.neeveGreen}
                  radius={[4, 4, 0, 0]}
                  barSize={32}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulative"
                  name="Total Deployments"
                  stroke={NEEVE.orange}
                  strokeWidth={2}
                  dot={{ fill: NEEVE.orange, r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
}
